/**
 * assets/sticky-scroll.js
 * ─────────────────────────────────────────────────────────
 * Efecto sticky-scroll apilado (estilo Loewe) — Misui theme
 * Vanilla JS puro, sin dependencias.
 *
 * v4 — Agrupación GLOBAL por toggle, ignora elementos intermedios.
 *
 * Diferencia clave respecto a v2/v3:
 *   v2/v3 agrupaba solo secciones .ss-section que fueran
 *   HERMANAS CONSECUTIVAS en el DOM (si había algo sin toggle
 *   entre medias, cortaba el grupo).
 *
 *   v4 agrupa TODAS las .ss-section de la página en un único
 *   track, en el orden en que aparecen — sin importar si hay
 *   secciones sin el toggle activado físicamente en medio.
 *   Esas secciones intermedias (ej. Appointment Banner sin
 *   toggle) se quedan en su sitio, fuera del track, y el scroll
 *   las salta visualmente porque las .ss-section se mueven a
 *   un único contenedor común insertado en la posición de la
 *   PRIMERA .ss-section encontrada.
 *
 * Requisito en el .liquid de cada sección participante:
 *   <section class="bau ss-section" ...>
 *   <section class="ooak ss-section" ...>
 *   <section class="appointment-banner ss-section" ...>
 * (la clase se añade condicionalmente según el checkbox
 *  enable_sticky_scroll del schema)
 * ─────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  /* ── Config ─────────────────────────────────────────── */
  const SECTION_SELECTOR = '.ss-section';
  const MOBILE_BP        = 750;
  /*
    ENTRY_DURATION: qué fracción de un vh-de-scroll dura la
    animación de entrada de cada sección.
    0.4 → la sección sube en los primeros 40% del slot,
          luego queda quieta arriba el resto del scroll.
    Sube desde 0.4 para más rapidez, baja para más lentitud.
  */
  const ENTRY_DURATION   = 0.2;

  /* ── Estado ─────────────────────────────────────────── */
  let track    = null;   // un único track global
  let sections = [];     // todas las .ss-section, en orden DOM
  let vh       = 0;

  function ease(t) {
    return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
  }

  function clamp(val, min, max) {
    return Math.max(min, Math.min(max, val));
  }

  function isActive() {
    return window.innerWidth >= MOBILE_BP
      && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /*
    Cada sección custom de Shopify vive dentro de
    <div id="shopify-section-{{ section.id }}" class="shopify-section ...">
    Movemos ese wrapper completo, no el <section> interno —
    así el editor de temas sigue reconociendo límites de sección,
    anclas, y el bloque [data-shopify-editor-block] funciona bien.
  */
  function getShopifySectionWrapper(el) {
    return el.closest('[id^="shopify-section-"]') || el.parentElement;
  }

  /* ── Construye un único track global ─────────────────── */
  function buildTrack() {
    /* Limpiar cualquier track anterior (ej. tras reorder en el editor) */
    if (track && track.parentNode) {
      const frag = document.createDocumentFragment();
      while (track.firstChild) frag.appendChild(track.firstChild);
      track.parentNode.insertBefore(frag, track);
      track.parentNode.removeChild(track);
    }
    track    = null;
    sections = [];

    const sectionEls = Array.from(document.querySelectorAll(SECTION_SELECTOR));
    if (sectionEls.length < 2) return;

    const wrappers = sectionEls.map(getShopifySectionWrapper);

    /* Insertar el track en la posición de la primera sección con toggle */
    track = document.createElement('div');
    track.className = 'ss-track';

    const firstWrapper = wrappers[0];
    firstWrapper.parentNode.insertBefore(track, firstWrapper);

    /* Mover cada wrapper (estén donde estén) dentro del track,
       en el mismo orden en que aparecían en el DOM */
    wrappers.forEach(function (el) {
      track.appendChild(el);
    });

    sections = wrappers;
  }

  /*
    DIM_OPACITY: opacidad mínima que alcanza la sección cuando queda
    cubierta. 1 = sin cambio, 0 = desaparece del todo.
  */
  const DIM_OPACITY    = 1;
  /*
    DIM_BRIGHTNESS: oscurecimiento (gris sombreado) al quedar debajo.
    1 = sin cambio, 0.5 = mitad de brillo.
  */
  const DIM_BRIGHTNESS = 0.7;

  /* ── Setup: alturas iniciales + z-index ──────────────────── */
  function setup() {
    if (!track || sections.length < 2) return;

    vh = window.innerHeight;
    const n = sections.length;

    if (!isActive()) {
      track.style.height = '';
      sections.forEach(function (sec) {
        sec.style.position   = '';
        sec.style.top        = '';
        sec.style.height     = '';
        sec.style.transform  = '';
        sec.style.zIndex     = '';
        sec.style.opacity    = '';
        sec.style.filter     = '';
      });
      return;
    }

    track.style.height = (vh * n) + 'px';

    sections.forEach(function (sec, i) {
      sec.style.position   = 'sticky';
      sec.style.top        = '0';
      sec.style.height     = '100svh';
      sec.style.overflow   = 'hidden';
      sec.style.zIndex     = i + 1;
      sec.style.transform  = i === 0 ? 'translateY(0%)' : 'translateY(100%)';
      sec.style.opacity    = '1';
      sec.style.filter     = 'brightness(1)';
    });
  }

  /* ── Render frame ─────────────────────────────────────────── */
  function render() {
    if (!track || sections.length < 2 || !isActive()) return;

    const trackTop = track.getBoundingClientRect().top
      + (window.scrollY || window.pageYOffset);
    const local = (window.scrollY || window.pageYOffset) - trackTop;

    sections.forEach(function (sec, i) {
      if (i === 0) return;

      /*
        slotStart: scroll local en que empieza la animación de la sección i.
        animEnd:   scroll local en que termina (dura ENTRY_DURATION × vh).
      */
      const slotStart = vh * (i - 0.9);
      const animEnd   = slotStart + vh * ENTRY_DURATION;

      const progress   = clamp((local - slotStart) / (animEnd - slotStart), 0, 1);
      const eased      = ease(progress);

      /* ── Sección entrante (i): sube desde abajo ── */
      const translateY = (1 - eased) * 100;
      sec.style.transform = 'translateY(' + translateY.toFixed(3) + '%)';
      sec.style.opacity   = '1';
      sec.style.filter    = 'brightness(1)';

      /* ── Sección saliente (i-1): se difumina y oscurece ── */
      var prev = sections[i - 1];
      if (prev) {
        var opacity    = 1 - (1 - DIM_OPACITY)    * eased;
        var brightness = 1 - (1 - DIM_BRIGHTNESS) * eased;
        prev.style.opacity = opacity.toFixed(3);
        prev.style.filter  = 'brightness(' + brightness.toFixed(3) + ')';
      }
    });
  }

  let ticking = false;
  function onScroll() {
    if (!ticking) {
      window.requestAnimationFrame(function () {
        render();
        ticking = false;
      });
      ticking = true;
    }
  }

  let resizeTimer;
  function onResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      setup();
      render();
    }, 150);
  }

  function init() {
    buildTrack();
    if (!track) return;

    setup();
    render();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Re-init si el theme editor reordena/edita secciones o
     se activa/desactiva el checkbox enable_sticky_scroll */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function () {
      setTimeout(init, 100);
    });
    document.addEventListener('shopify:section:unload', function () {
      setTimeout(init, 100);
    });
    document.addEventListener('shopify:section:reorder', function () {
      setTimeout(init, 100);
    });
    document.addEventListener('shopify:section:select', function () {
      setTimeout(init, 100);
    });
  }

})();
